import React, { useState, useEffect } from 'react';
import { FaTag, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSearch } from 'react-icons/fa';
import { categoriesApi } from '../services/api';
import './CategoryManagement.css';

const CategoryManagement = () => {
    // State for categories list
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for form
    const [formMode, setFormMode] = useState('add');
    const [currentCategory, setCurrentCategory] = useState({
        id: null,
        name: '',
        description: ''
    });
    
    // State for search
    const [searchTerm, setSearchTerm] = useState('');
    
    // State for showing form modal
    const [showForm, setShowForm] = useState(false);
    
    // State for editing category
    const [editingCategoryId, setEditingCategoryId] = useState(null);

    // Fetch categories when component mounts
    useEffect(() => {
        fetchCategories();
    }, []);

    // Fetch categories from API
    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await categoriesApi.getAll();
            console.log(response);
            // Update to handle the correct response structure
            setCategories(Array.isArray(response.data.data.categories) ? response.data.data.categories : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError('Failed to fetch categories. Please try again later.');
            setCategories([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    // Filter categories based on search term
    const filteredCategories = Array.isArray(categories) ? categories.filter(category => 
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description.toLowerCase().includes(searchTerm.toLowerCase())
    ) : [];

    // Reset form to default state
    const resetForm = () => {
        setCurrentCategory({
            id: null,
            name: '',
            description: ''
        });
        setFormMode('add');
        setEditingCategoryId(null);
    };

    // Open form for adding a new category
    const openAddForm = () => {
        resetForm();
        setShowForm(true);
    };

    // Open form for editing an existing category
    // const openEditForm = (category) => {
    //     setCurrentCategory({...category});
    //     setFormMode('edit');
    //     setShowForm(true);
    // };

    // Handle inline editing
    const startInlineEdit = (category) => {
        setEditingCategoryId(category.id);
        setCurrentCategory({...category});
    };

    // Cancel inline editing
    const cancelInlineEdit = () => {
        setEditingCategoryId(null);
        resetForm();
    };

    // Handle input change for form
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentCategory({
            ...currentCategory,
            [name]: value
        });
    };

    // Handle form submission
    const handleFormSubmit = async (e) => {
        e.preventDefault(); // Ensure we prevent default form submission
        
        if (!currentCategory.name.trim()) {
            alert('Category name is required');
            return;
        }
        
        try {
            if (formMode === 'add') {
                await categoriesApi.create(currentCategory);
                console.log('Category created successfully');
            } else {
                await categoriesApi.update(currentCategory.id, currentCategory);
                console.log('Category updated successfully');
            }
            
            // Refresh categories list
            await fetchCategories();
            
            // Close form and reset
            setShowForm(false);
            resetForm();
            setError(null);
        } catch (err) {
            console.error('Error saving category:', err.response?.data || err.message);
            setError(err.response?.data?.message || 'Failed to save category. Please try again.');
        }
    };

    // Save inline edit
    const saveInlineEdit = async () => {
        if (!currentCategory.name.trim()) {
            alert('Category name is required');
            return;
        }
        
        try {
            await categoriesApi.update(currentCategory.id, currentCategory);
            await fetchCategories();
            setEditingCategoryId(null);
            resetForm();
            setError(null);
        } catch (err) {
            setError('Failed to update category. Please try again.');
            console.error('Error updating category:', err);
        }
    };

    // Delete a category
    const deleteCategory = async (categoryId) => {
        if (window.confirm('Are you sure you want to delete this category? This may affect products using this category.')) {
            try {
                await categoriesApi.delete(categoryId);
                await fetchCategories();
                setError(null);
            } catch (err) {
                setError('Failed to delete category. Please try again.');
                console.error('Error deleting category:', err);
            }
        }
    };

    if (loading) {
        return <div className="loading">Loading categories...</div>;
    }

    return (
        <div className="category-management-container">
            <h1><FaTag className="icon-space" /> Category Management</h1>
            
            {error && <div className="error-message">{error}</div>}
            
            {/* Search and Add Bar */}
            <div className="search-add-bar">
                <div className="search-box">
                    <FaSearch className="search-icon" />
                    <input 
                        type="text" 
                        placeholder="Search categories..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                
                <button type="button" className="btn-add" onClick={openAddForm}>
                    <FaPlus /> Add Category
                </button>
            </div>
            
            {/* Categories Table */}
            <div className="categories-table-container">
                {filteredCategories && filteredCategories.length > 0 ? (
                    <table className="categories-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Description</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategories.map((category) => (
                                <tr key={category.id}>
                                    <td>
                                        {editingCategoryId === category.id ? (
                                            <input 
                                                type="text" 
                                                name="name" 
                                                value={currentCategory.name} 
                                                onChange={handleInputChange}
                                                className="inline-edit-input"
                                            />
                                        ) : (
                                            category.name
                                        )}
                                    </td>
                                    <td>
                                        {editingCategoryId === category.id ? (
                                            <input 
                                                type="text" 
                                                name="description" 
                                                value={currentCategory.description} 
                                                onChange={handleInputChange}
                                                className="inline-edit-input"
                                            />
                                        ) : (
                                            category.description || '-'
                                        )}
                                    </td>
                                    <td className="actions-cell">
                                        {editingCategoryId === category.id ? (
                                            <>
                                                <button 
                                                    className="btn-action btn-save" 
                                                    onClick={saveInlineEdit}
                                                    title="Save"
                                                >
                                                    <FaSave />
                                                </button>
                                                <button 
                                                    className="btn-action btn-cancel" 
                                                    onClick={cancelInlineEdit}
                                                    title="Cancel"
                                                >
                                                    <FaTimes />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button 
                                                    className="btn-action btn-edit" 
                                                    onClick={() => startInlineEdit(category)}
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button 
                                                    className="btn-action btn-delete" 
                                                    onClick={() => deleteCategory(category.id)}
                                                    title="Delete"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="no-categories">
                        {searchTerm ? 'No categories match your search.' : 'No categories added yet.'}
                    </div>
                )}
            </div>
            
            {/* Category Form Modal */}
            {showForm && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-content">
                            <h2>{formMode === 'add' ? 'Add New Category' : 'Edit Category'}</h2>
                            <form onSubmit={handleFormSubmit} noValidate>
                                <div className="form-group">
                                    <label>Name:</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={currentCategory.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description:</label>
                                    <textarea
                                        name="description"
                                        value={currentCategory.description}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-actions">
                                    <button type="submit" className="btn-save">
                                        {formMode === 'add' ? 'Add Category' : 'Update Category'}
                                    </button>
                                    <button type="button" className="btn-cancel" onClick={() => {
                                        setShowForm(false);
                                        resetForm();
                                    }}>
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryManagement; 